'use client';
/**
 * Cray26 Theme — Drawer Navigation Component
 * Hamburger menu visible on all breakpoints; shows structured sections with nested children.
 */

import React, { useEffect, useRef, useState } from 'react';

import Icon from './Icon';
import Link from './Link';

export type NavItem = {
  id: number;
  href?: string;
  label: string;
  icon?: string;
  className?: string;
  target?: string;
  rel?: string;
  children?: NavItem[];
};

export type NavSection = {
  label?: string;
  className?: string;
  items: NavItem[];
};

export type MobileNavigationProps = {
  sections?: NavSection[];
};

type NavItemRowProps = {
  item: NavItem;
  onClose: () => void;
  depth?: number;
};

const NavItemRow: React.FC<NavItemRowProps> = ({ item, onClose, depth = 0 }) => {
  const hasChildren = item.children && item.children.length > 0;

  return (
    <li>
      <Link
        href={item.href}
        target={item.target}
        rel={item.rel}
        className={`
          flex flex-row items-center gap-2 py-2 px-1 rounded-md transition-all duration-200
          hover:font-semibold active:scale-95
          ${depth > 0 ? 'text-sm text-gray-800' : ''}
          ${hasChildren ? 'font-medium' : ''}
          ${item.className ?? ''}
        `}
        onClick={onClose}
      >
        {item.icon && (
          <span className="shrink-0">
            <Icon name={item.icon} size={depth === 0 ? 20 : 16} />
          </span>
        )}
        <span>{item.label}</span>
        {hasChildren && (
          <span className="ml-auto shrink-0 text-gray-400">
            <Icon name="chevron-right" size={12} />
          </span>
        )}
      </Link>

      {hasChildren && (
        <ul className="ml-3 pl-3 border-l-2 border-gray-200 flex flex-col gap-0.5 mb-1 animate-slide-in-left">
          {item.children!.map((child) => (
            <NavItemRow key={child.id} item={child} onClose={onClose} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
};

export const MobileNavigation: React.FC<MobileNavigationProps> = ({ sections = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const toggleMenu = () => setIsOpen((prev) => !prev);
  const closeMenu = () => setIsOpen(false);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <nav className="mobile-navigation">
      {/* Toggle button */}
      <div className="flex flex-row justify-center">
        <button
          ref={buttonRef}
          onClick={toggleMenu}
          className="menu-toggle-button p-4 focus:outline-none cursor-pointer"
          aria-label={isOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={isOpen}
          aria-controls="drawer-menu"
        >
          <Icon name={isOpen ? 'close' : 'menu'} size={24} />
        </button>
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm animate-fade-in"
          aria-hidden="true"
          onClick={closeMenu}
        />
      )}

      {/* Drawer */}
      {isOpen && (
        <div
          id="drawer-menu"
          ref={menuRef}
          role="dialog"
          aria-modal="true"
          aria-label="Menú de navegación"
          className="
            mobile-menu
            fixed top-0 left-0 z-50
            h-full w-80 max-w-[90vw]
            bg-white shadow-2xl
            flex flex-col
            animate-slide-in-left
            overflow-hidden
          "
        >
          {/* Drawer header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <span className="text-sm font-semibold tracking-wide text-gray-500 uppercase">
              Menú
            </span>
            <button
              onClick={closeMenu}
              className="p-1.5 rounded-full hover:bg-gray-100 transition-colors duration-200"
              aria-label="Cerrar menú"
            >
              <Icon name="close" size={18} />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto overscroll-contain px-10 py-8 flex flex-col gap-5">
            {sections.map((section, sectionIdx) => (
              <div key={sectionIdx} className={`flex flex-col gap-1`}>
                {section.label && (
                  <p className="text-xs font-bold tracking-widest uppercase text-gray-600 mb-1 px-1">
                    {section.label}
                  </p>
                )}
                <ul className={`flex flex-col gap-2 ${section.className || ''}`}>
                  {section.items.map((item) => (
                    <NavItemRow key={item.id} item={item} onClose={closeMenu} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default MobileNavigation;
